import type { DeductionCreditMemo, DeductionCreditMemoLineItem } from '@api/models';
import { toTypedSchema } from '@vee-validate/zod';
import * as changeCase from 'change-case';
import { defineStore } from 'pinia';
import { useForm } from 'vee-validate';
import * as zod from 'zod';

import { sumArray } from '@stuut/common/src/utils';
import { toast } from '@ui/components/sonner';

import { stuut } from '@/api';

const lineItemValidationSchema = toTypedSchema(
  zod.object({
    currency: zod.string().nullable(),
    description: zod.string().nullable(),
    gross_amount: zod.union([zod.coerce.number(), zod.string()]).nullable(),
    price: zod.union([zod.coerce.number(), zod.string()]).nullable(),
    quantity: zod.union([zod.coerce.number(), zod.string()]).nullable(),
    reason: zod.string().optional().nullable(),
    reason_id: zod.string().optional().nullable(),
    validation_code: zod.string().optional().nullable(),
    vendor_id: zod.string().optional().nullable(),
  }),
);

export interface FormDeductionCreditMemoLineItem {
  formData: any;
  id: string;
  isDeleted?: boolean;
  isDeleting?: boolean;
  isDestroyed?: boolean;
  isDisabled?: boolean;
  isEditing?: boolean;
  isSaving?: boolean;
  isUndoing?: boolean;
  lineItem: DeductionCreditMemoLineItem;
}

interface State {
  creditMemo: DeductionCreditMemo | null;
  creditMemoLineItems: FormDeductionCreditMemoLineItem[];
  deletedCreditMemoLineItems: FormDeductionCreditMemoLineItem[];
  isEditDisabled: boolean;
  isUndoing: boolean;
}

export const useDeductionsCreditMemos = defineStore('useDeductionsCreditMemos', {
  actions: {
    createFormData(creditMemoLineItem: DeductionCreditMemoLineItem) {
      return useForm({
        initialValues: {
          currency: creditMemoLineItem?.currency,
          description: creditMemoLineItem?.description,
          gross_amount: creditMemoLineItem?.gross_amount,
          price: creditMemoLineItem?.price,
          quantity: creditMemoLineItem?.quantity,
          reason: creditMemoLineItem?.reason?.code,
          reason_id: creditMemoLineItem?.reason?.id,
          validation_code: creditMemoLineItem?.custom_fields?.validation_code?.value,
          vendor_id: creditMemoLineItem?.vendor?.id,
        },
        validationSchema: lineItemValidationSchema,
      });
    },

    createFormDeductionCreditMemoLineItem(lineItem: DeductionCreditMemoLineItem) {
      return {
        formData: this.createFormData(lineItem),
        id: lineItem?.id,
        isDisabled: false,
        isEditing: false,
        lineItem,
      };
    },

    async createLineItem(creditMemoId: string, data?: Partial<DeductionCreditMemoLineItem>) {
      if (!creditMemoId) return;

      try {
        const lineItem = await stuut.deductionsCreditMemoLineItems.create({
          credit_memo_id: creditMemoId,
          ...(data || {}),
        });

        const formLineItem = this.createFormDeductionCreditMemoLineItem(lineItem);

        this.creditMemoLineItems = [...this.creditMemoLineItems, formLineItem];
      } catch (err: any) {
        toast.error(err.clientMessage || err.message);
      }
    },

    async deleteItem(row) {
      row.original.isDeleting = true;

      try {
        const response = await stuut.deductionsCreditMemoLineItems.delete(row.id);
        row.original.isDeleted = true;
        return response;
      } finally {
        row.original.isDeleting = false;
      }
    },

    discardEdit(row) {
      const creditMemo = this.getById(row.id);

      if (!creditMemo?.id) return;

      creditMemo.formData.handleReset();
      creditMemo.isEditing = false;

      return creditMemo;
    },

    endEdit(id: string) {
      const creditMemoLineItem = this.getById(id);

      if (!creditMemoLineItem?.id) return;

      creditMemoLineItem.isEditing = false;

      return creditMemoLineItem;
    },

    async refreshCreditMemo(userInstructions?: string) {
      this.isEditDisabled = true;

      try {
        if (!this.creditMemo?.id) return;

        await stuut.deductionsCreditMemos.refresh(
          this.creditMemo.id,
          this.creditMemo,
          userInstructions,
        );

        await this.setupLineItems(this.creditMemo.line_items);

        toast.success('Credit Memo refreshed!');

        return this.creditMemo;
      } finally {
        this.isEditDisabled = false;
      }
    },

    async saveEdit(row) {
      const creditMemoLineItem = this.getById(row.id);

      if (!creditMemoLineItem?.id) return;

      const validationResult = await row.original.formData.validate();

      if (!validationResult.valid) {
        const invalidFields = Object.keys(validationResult.results)
          .filter((key) => validationResult?.results?.[key]?.valid === false)
          .map((field) => changeCase.sentenceCase(field));

        throw Error(`Fix these fields and retry: ${invalidFields.join(', ')}`);
      }

      const { validation_code: validationCode, ...restValues } = creditMemoLineItem.formData.values;
      const updateData = {
        ...restValues,
        ...(validationCode !== undefined && {
          custom_fields: {
            ...(creditMemoLineItem.lineItem as any)?.custom_fields,
            validation_code: validationCode,
          },
        }),
      };
      const response = await stuut.deductionsCreditMemoLineItems.update(
        creditMemoLineItem.id,
        updateData,
        creditMemoLineItem.lineItem,
      );

      this.updateRow(response);

      this.endEdit(creditMemoLineItem.id);

      return response;
    },

    async setup(creditMemo: DeductionCreditMemo) {
      this.creditMemo = creditMemo;
      const lineItems = this.setupLineItems(this.creditMemo.line_items);

      return this.creditMemo;
    },

    async setupLineItems(lineItems: DeductionCreditMemoLineItem[]) {
      if (!lineItems?.length) return;

      const creditMemoLineItems = lineItems?.map((item, index) => {
        return this.createFormDeductionCreditMemoLineItem(item);
      });

      this.creditMemoLineItems = creditMemoLineItems;

      return this.creditMemoLineItems;
    },

    startEdit(id: string) {
      if (this.isEditDisabled || this.creditMemo?.is_updating) return;

      const creditMemoLineItem = this.getById(id);

      if (!creditMemoLineItem?.id || creditMemoLineItem?.isDisabled) return;

      creditMemoLineItem.isEditing = true;

      return creditMemoLineItem;
    },

    async undoDelete(row) {
      if (!this.creditMemo?.id) return;

      row.original.isUndoing = true;

      try {
        const response = await this.createLineItem(
          this.creditMemo?.id,
          row.original?.formData?.values,
        );

        row.original.isDestroyed = true;

        return response;
      } catch (err: any) {
        toast.error(err.clientMessage || err.message);
      } finally {
        row.original.isUndoing = false;
      }
    },

    updateRow(creditMemoLineItem: DeductionCreditMemoLineItem) {
      const index = this.creditMemoLineItems.findIndex((item) => item.id === creditMemoLineItem.id);
      const existingItem = this.creditMemoLineItems[index];

      if (index > -1) {
        const newItems = [...this.creditMemoLineItems];

        newItems[index] = this.createFormDeductionCreditMemoLineItem(creditMemoLineItem);

        this.creditMemoLineItems = newItems;
      }
    },
  },

  getters: {
    activeLineItems: (state) => {
      return state.creditMemoLineItems?.filter((item) => !item.isDestroyed && !item.isDeleted);
    },

    getById: (state) => {
      return (id: string) => state.creditMemoLineItems.find((item) => item.id === id);
    },

    lineItemsTotal: (state) => {
      const activeItems = state.creditMemoLineItems?.filter(
        (item) => !item.isDestroyed && !item.isDeleted,
      );

      return sumArray(
        activeItems?.map((item) => {
          const amount = item?.formData?.values?.gross_amount;

          if (!amount) return 0;

          const num = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
          return isNaN(num) ? 0 : num;
        }),
      ) as number;
    },

    visibleLineItems: (state) => {
      return state.creditMemoLineItems?.filter((item) => !item.isDestroyed);
    },
  },

  state: (): State => ({
    creditMemo: null,
    creditMemoLineItems: [],
    deletedCreditMemoLineItems: [],
    isEditDisabled: false,
    isUndoing: false,
  }),
});

export default useDeductionsCreditMemos;
